import { useEffect, useRef, useState } from "react";
import { tr } from "../../i18n/tr";
import { Loader2, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Lecteur SCORM 1.2 / 2004 — expose window.API et window.API_1484_11 pour le
// contenu chargé dans l'iframe (même origine), et committe le cmi au backend.
export const ScormPlayer = ({ sequence, enrollmentId, api, onCompleted, studentName }) => {
  const [ready, setReady] = useState(false);
  const [completed, setCompleted] = useState(false);
  const cmiRef = useRef({});
  const completedRef = useRef(false);
  const commitTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const doCommit = async (finish = false) => {
      if (!enrollmentId) return;
      try {
        const r = await api.post(
          `/lms/student/enrollments/${enrollmentId}/scorm/${sequence.sequence_id}/commit`,
          { cmi: cmiRef.current, finish },
          { dedupe: true }
        );
        let done = !!r.data?.completed;
        if (r.queued) {
          // Hors ligne : complétion déduite localement + cache du tracking mis à jour
          const st = String(cmiRef.current["cmi.core.lesson_status"] || cmiRef.current["cmi.completion_status"] || cmiRef.current["cmi.success_status"] || "").toLowerCase();
          done = st === "completed" || st === "passed";
          api.patchCache?.(`/lms/student/enrollments/${enrollmentId}/scorm/${sequence.sequence_id}/tracking`, (d) => ({ ...(d || {}), cmi: { ...cmiRef.current }, completed: done || !!d?.completed }));
        }
        if (done && !completedRef.current) {
          completedRef.current = true;
          setCompleted(true);
          onCompleted?.({ queued: !!r.queued });
        }
      } catch { /* offline commit tolerated */ }
    };

    const scheduleCommit = () => {
      if (commitTimer.current) clearTimeout(commitTimer.current);
      commitTimer.current = setTimeout(() => doCommit(false), 1500);
    };

    const defaults = {
      "cmi.core.student_name": studentName || "",
      "cmi.core.student_id": enrollmentId || "preview",
      "cmi.learner_name": studentName || "",
      "cmi.learner_id": enrollmentId || "preview",
      "cmi.core.lesson_status": "not attempted",
      "cmi.completion_status": "unknown",
      "cmi.core.lesson_mode": "normal",
      "cmi.mode": "normal",
      "cmi.core.credit": "credit",
      "cmi.credit": "credit",
      "cmi.core.entry": "ab-initio",
      "cmi.entry": "ab-initio",
    };

    const getValue = (key) => {
      const k = String(key);
      if (k in cmiRef.current) return cmiRef.current[k];
      if (k.endsWith("._children") || k.endsWith("._count")) return "";
      return defaults[k] ?? "";
    };

    const setValue = (key, value) => {
      const k = String(key);
      cmiRef.current[k] = String(value);
      const v = String(value).toLowerCase();
      if ((k.endsWith("lesson_status") || k.endsWith("completion_status") || k.endsWith("success_status")) &&
          (v === "completed" || v === "passed")) {
        if (commitTimer.current) clearTimeout(commitTimer.current);
        doCommit(false);
      }
      return "true";
    };

    window.API = {
      LMSInitialize: () => "true",
      LMSFinish: () => { doCommit(true); return "true"; },
      LMSGetValue: getValue,
      LMSSetValue: setValue,
      LMSCommit: () => { scheduleCommit(); return "true"; },
      LMSGetLastError: () => "0",
      LMSGetErrorString: () => "No error",
      LMSGetDiagnostic: () => "",
    };
    window.API_1484_11 = {
      Initialize: () => "true",
      Terminate: () => { doCommit(true); return "true"; },
      GetValue: getValue,
      SetValue: setValue,
      Commit: () => { scheduleCommit(); return "true"; },
      GetLastError: () => "0",
      GetErrorString: () => "No error",
      GetDiagnostic: () => "",
    };

    const load = async () => {
      if (enrollmentId) {
        try {
          const r = await api.get(`/lms/student/enrollments/${enrollmentId}/scorm/${sequence.sequence_id}/tracking`);
          cmiRef.current = r.data?.cmi || {};
          if (r.data?.completed) { completedRef.current = true; setCompleted(true); }
        } catch { /* no tracking yet */ }
      }
      if (!cancelled) setReady(true);
    };
    load();

    return () => {
      cancelled = true;
      if (commitTimer.current) clearTimeout(commitTimer.current);
      doCommit(true);
      delete window.API;
      delete window.API_1484_11;
    };
    // eslint-disable-next-line
  }, [sequence.sequence_id, enrollmentId]);

  if (!sequence.scorm_entry) {
    return (
      <div className="text-slate-400 text-sm p-4 border border-dashed border-white/10 rounded" data-testid="scorm-no-package">
        {tr("lms.aucun_package_scorm_importe_6aec", "Aucun package SCORM importé pour ce module. Contactez votre administrateur.")}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="scorm-player">
      {completed && (
        <div className="flex items-center gap-2 text-green-400 text-xs bg-green-500/10 border border-green-500/30 rounded p-2" data-testid="scorm-completed-banner">
          <CheckCircle2 className="w-4 h-4" /> {tr("lms.module_scorm_complete_d60f", "Module SCORM complété")}
        </div>
      )}
      {!ready ? (
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>
      ) : (
        <iframe
          src={`${BACKEND}/api/lms/scorm/${sequence.sequence_id}/file/${sequence.scorm_entry}`}
          className="w-full h-[65vh] rounded bg-white"
          title={sequence.title || "SCORM"}
          allowFullScreen
          data-testid="scorm-iframe"
        />
      )}
      <div className="text-[10px] text-slate-500">SCORM {sequence.scorm_version || "1.2"} · progression enregistrée automatiquement</div>
    </div>
  );
};

export default ScormPlayer;
